import type {
  ClassInfo,
  CodeQualityAssessment,
  CodeQualitySignal,
  ExtractedString,
  MethodFingerprint,
} from "./types";

const explicitMarkers = [
  /as an ai language model/i,
  /generated (?:by|with) (?:chatgpt|claude|gemini|copilot)/i,
  /i hope this helps/i,
  /here(?:'s| is) the (?:code|implementation)/i,
  /```(?:java|kotlin)?/i,
];

const templateMarkers = [
  /todo:\s*(?:implement|add|handle)/i,
  /your (?:api key|code) here/i,
  /replace (?:this|with your)/i,
  /example (?:usage|implementation)/i,
];

function genericName(name: string) {
  return /^(?:main|app|application|manager|service|helper|utils?|handler|processor|data|model|controller)$/i.test(
    name,
  );
}

export function assessCodeQuality(
  classes: ClassInfo[],
  strings: ExtractedString[],
  fingerprints: MethodFingerprint[],
): CodeQualityAssessment {
  const signals: CodeQualitySignal[] = [];
  const searchableStrings = strings
    .map((item) => item.value)
    .filter((value) => value.length <= 1_000);
  const explicitEvidence = searchableStrings.filter((value) =>
    explicitMarkers.some((pattern) => pattern.test(value)),
  );
  if (explicitEvidence.length) {
    signals.push({
      id: "explicit-ai-marker",
      label: "Explicit generated-code marker",
      detail:
        "The archive contains text that directly references an AI coding assistant or generated code.",
      weight: 70,
      kind: "explicit-marker",
      evidence: explicitEvidence.slice(0, 8),
    });
  }

  const templateEvidence = searchableStrings.filter((value) =>
    templateMarkers.some((pattern) => pattern.test(value)),
  );
  if (templateEvidence.length >= 2) {
    signals.push({
      id: "template-placeholder-noise",
      label: "Template placeholder residue",
      detail:
        "Several implementation placeholders or example-code phrases remain in the archive.",
      weight: Math.min(20, 6 + templateEvidence.length * 2),
      kind: "template-noise",
      evidence: templateEvidence.slice(0, 8),
    });
  }

  const fingerprintGroups = new Map<string, MethodFingerprint[]>();
  for (const fingerprint of fingerprints) {
    const key =
      fingerprint.semanticSha256 ||
      fingerprint.opcodeSha256 ||
      fingerprint.simhash64;
    if (!key) continue;
    const group = fingerprintGroups.get(key) ?? [];
    group.push(fingerprint);
    fingerprintGroups.set(key, group);
  }
  const duplicateGroups = Array.from(fingerprintGroups.values()).filter(
    (group) => group.length >= 4,
  );
  if (duplicateGroups.length >= 2) {
    signals.push({
      id: "duplicate-method-templates",
      label: "Repeated method templates",
      detail:
        "Multiple groups of methods share the same normalized implementation.",
      weight: Math.min(18, duplicateGroups.length * 3),
      kind: "duplication",
      evidence: duplicateGroups
        .slice(0, 6)
        .map((group) => `${group.length} equivalent methods`),
    });
  }

  const genericClasses = classes.filter((item) =>
    genericName(item.simpleName),
  );
  const readableNames = classes.filter((item) =>
    /^[A-Za-z][A-Za-z0-9_$]{3,}$/.test(item.simpleName),
  );
  if (
    genericClasses.length >= 6 &&
    genericClasses.length / Math.max(1, readableNames.length) >= 0.3
  ) {
    signals.push({
      id: "generic-class-naming",
      label: "Generic class naming",
      detail:
        "A large share of readable class names use generic template-style roles.",
      weight: Math.min(12, genericClasses.length),
      kind: "generic-naming",
      evidence: genericClasses.slice(0, 10).map((item) => item.name),
    });
  }

  const score = Math.min(
    100,
    signals.reduce((sum, signal) => sum + signal.weight, 0),
  );
  const hasExplicitMarker = signals.some(
    (signal) => signal.kind === "explicit-marker",
  );
  const label: CodeQualityAssessment["label"] = hasExplicitMarker
    ? "strong explicit markers"
    : score >= 35
      ? "notable signals"
      : score >= 15
        ? "weak signals"
        : "unlikely";

  return {
    score,
    label,
    confidence: hasExplicitMarker ? "high" : score >= 35 ? "medium" : "low",
    signals,
    duplicateMethodGroups: duplicateGroups.length,
    disclaimer:
      "These are code-quality and provenance signals, not proof that AI generated the code. Obfuscation, templates, generators, and copied examples can produce similar patterns. This assessment never changes the malware verdict.",
  };
}
